import coreUtils from '@opentripplanner/core-utils';
import { useIntl } from 'react-intl';
import useIntlOtpUiConfig from '#/trip-planner/hooks/useIntlOtpUiConfig';
import { EXCLUSIVE_CATEGORY_TYPE } from '#/otp-ui-config';
import {
  CAR_HAIL_MODE,
  PARK_AND_RIDE_MODE,
  BICYCLE_RENT_MODE,
  BICYCLE_MODE,
  MAIN_TRANSIT_MODE,
} from '#/shared/constants';

const { isAccessMode, isWalk } = coreUtils.itinerary;

const useModeLabel = () => {
  const { modes: modeConfig } = useIntlOtpUiConfig();
  const intl = useIntl();

  return {
    /**
     * Get user-friendly label describing selected modes based on the supplied
     * OTP mode string
     */
    getLabelFromModeString: modeString => {
      const selectedModes = modeString.split(',');
      const isExclusive = selectedModes.length === 1;
      const accessMode = selectedModes.find(m => isAccessMode(m));

      // if all transit modes are selected use the default transit label
      // otherwise create a label containing only the selected transit modes
      const transitLabel = modeConfig.transitModes.every(
        tmConfig =>
          selectedModes.includes(MAIN_TRANSIT_MODE) ||
          selectedModes.some(m => m === tmConfig.mode) ||
          tmConfig.hidden
      )
        ? intl.formatMessage({ id: 'planner.transit-mode-label' })
        : selectedModes
            .map(m =>
              modeConfig.transitModes.find(tmConfig => tmConfig.mode === m)
            )
            .filter(tmConfig => !!tmConfig && !tmConfig.hidden)
            .map(tmConfig => tmConfig.shortName)
            .sort()
            .join(', ');

      if (!accessMode || (isWalk(accessMode) && !isExclusive)) {
        return transitLabel;
      }

      const category = modeConfig.categories.find(
        c =>
          (c.mode === accessMode ||
            (c.options && c.options.find(o => o.mode === accessMode))) &&
          (c.type === EXCLUSIVE_CATEGORY_TYPE) === isExclusive
      );

      const transitPrefix = isExclusive ? '' : `${transitLabel} +\n`;
      let categoryLabel = category.label;

      if (!category.mode) {
        let modeIntlId = null;

        if (selectedModes.includes(CAR_HAIL_MODE)) {
          modeIntlId = 'ride-hail-mode-name';
        } else if (selectedModes.includes(PARK_AND_RIDE_MODE)) {
          modeIntlId = 'park-and-ride-mode-name';
        } else if (selectedModes.includes(BICYCLE_RENT_MODE)) {
          modeIntlId = isExclusive
            ? 'bicycle-rental-mode-exclusive'
            : 'bicycle-rental-mode-name';
        } else if (selectedModes.includes(BICYCLE_MODE)) {
          modeIntlId = isExclusive
            ? 'bicycle-mode-exclusive'
            : 'bicycle-mode-name';
        }

        if (modeIntlId) {
          categoryLabel = intl.formatMessage({ id: `planner.${modeIntlId}` });
        }
      }

      return `${transitPrefix} ${categoryLabel.replace('+ ', '')}`.trim();
    },
  };
};

export default useModeLabel;
